/**
 * WebDAVClient — Native fetch-based WebDAV client
 * No external dependencies — uses browser APIs directly
 * @module webdav/webdav-client
 */

import { loadJSON, saveJSON } from '../utils/storage.js';

const PROFILES_KEY = 'mypad_webdav_profiles';

export class WebDAVClient {
  constructor() {
    /** @type {string|null} */
    this._baseUrl = null;
    /** @type {string|null} */
    this._authHeader = null;
    /** @type {boolean} */
    this._connected = false;
    /** @type {Array<string>|null} */
    this._indexCache = null;
    /** @type {string} */
    this._indexPath = '/webdav_index.json';
  }

  /**
   * Connect to a WebDAV server
   * @param {string} url - Base URL (e.g. https://dav.example.com/files)
   * @param {string} username
   * @param {string} password
   * @param {string} indexPath
   * @returns {Promise<boolean>}
   */
  async connect(url, username, password, indexPath = '/webdav_index.json') {
    // Normalize URL — remove trailing slash
    this._baseUrl = url.replace(/\/+$/, '');
    this._authHeader = 'Basic ' + btoa(username + ':' + password);
    this._indexPath = indexPath;

    try {
      // Test connection with a PROPFIND on root
      await this.listDirectory('/');
      this._connected = true;
      return true;
    } catch (e) {
      if (indexPath && (e.message.includes('405') || e.message.includes('Method Not Allowed') || e.message.includes('501'))) {
        try {
          await this.loadIndex();
          this._connected = true;
          return true;
        } catch (idxErr) {
          // Fall through to throw original error
        }
      }
      this._connected = false;
      this._baseUrl = null;
      this._authHeader = null;
      throw new Error(`Connection failed: ${e.message}`);
    }
  }

  /** Disconnect */
  disconnect() {
    this._baseUrl = null;
    this._authHeader = null;
    this._connected = false;
  }

  /** @returns {boolean} */
  isConnected() {
    return this._connected;
  }

  /**
   * List directory contents
   * @param {string} path
   * @returns {Promise<Array<{name:string, path:string, isDirectory:boolean, size:number, lastModified:string, contentType:string}>>}
   */
  async listDirectory(path) {
    const url = this._resolvePath(path);

    try {
      const response = await this._fetch(url, {
        method: 'PROPFIND',
        headers: {
          ...this._getHeaders(),
          'Depth': '1',
          'Content-Type': 'application/xml; charset=utf-8',
        },
        body: `<?xml version="1.0" encoding="UTF-8"?>
<D:propfind xmlns:D="DAV:">
  <D:prop>
    <D:displayname/>
    <D:getcontentlength/>
    <D:getlastmodified/>
    <D:getcontenttype/>
    <D:resourcetype/>
  </D:prop>
</D:propfind>`,
      });

      if (!response.ok && response.status !== 207) {
        throw new Error(`PROPFIND failed: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();
      return this._parsePropfindResponse(text, path);
    } catch (e) {
      if ((e.message.includes('405') || e.message.includes('Method Not Allowed') || e.message.includes('501')) && this._indexPath) {
        try {
          const index = await this.loadIndex();
          return this._synthesizeDirectoryFromIndex(path, index);
        } catch (idxErr) {
          throw new Error(`PROPFIND failed (${e.message}) and fallback index failed: ${idxErr.message}`);
        }
      }
      throw e;
    }
  }

  _synthesizeDirectoryFromIndex(path, index) {
    let dirPath = path;
    if (!dirPath.endsWith('/')) dirPath += '/';
    if (dirPath === '//') dirPath = '/';

    const items = new Map();

    for (const entry of index) {
      let p = '', size = 0, mtime = 0;
      if (Array.isArray(entry)) {
        p = entry[0] || '';
        size = entry[1] || 0;
        mtime = entry[2] || 0;
      } else if (typeof entry === 'object' && entry !== null) {
        p = entry.path || entry.p || '';
        size = entry.size ?? entry.s ?? 0;
        mtime = entry.mtime ?? entry.m ?? 0;
      } else {
        p = String(entry || '');
      }

      const fullPath = p.startsWith('/') ? p : '/' + p;
      if (fullPath.startsWith(dirPath) && fullPath !== dirPath) {
        const relative = fullPath.substring(dirPath.length);
        const parts = relative.split('/');
        const isDir = parts.length > 1 || p.endsWith('/');
        const name = parts[0];
        
        if (!items.has(name) && name) {
          items.set(name, {
            name: name,
            path: dirPath + name + (isDir ? '/' : ''),
            isDirectory: isDir,
            size: isDir ? 0 : size,
            lastModified: mtime || '',
            contentType: ''
          });
        }
      }
    }
    
    return Array.from(items.values()).sort((a, b) => {
      if (a.isDirectory && !b.isDirectory) return -1;
      if (!a.isDirectory && b.isDirectory) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Read a file
   * @param {string} path
   * @returns {Promise<ArrayBuffer>}
   */
  async readFile(path, onProgress) {
    const url = this._resolvePath(path);

    const response = await this._fetch(url, {
      method: 'GET',
      headers: this._getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`GET failed: ${response.status} ${response.statusText}`);
    }

    if (!onProgress || !response.body) {
      return response.arrayBuffer();
    }

    const contentLength = response.headers.get('content-length') || response.headers.get('x-original-content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;
    let loaded = 0;
    
    const reader = response.body.getReader();
    const chunks = [];
    const startTime = Date.now();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        loaded += value.length;
        onProgress(loaded, total, startTime);
      }
    }

    const arrayBuffer = new Uint8Array(loaded);
    let offset = 0;
    for (const chunk of chunks) {
      arrayBuffer.set(chunk, offset);
      offset += chunk.length;
    }
    return arrayBuffer.buffer;
  }

  /**
   * Write/upload a file
   * @param {string} path
   * @param {ArrayBuffer|Uint8Array|string} data
   * @returns {Promise<void>}
   */
  async writeFile(path, data) {
    const url = this._resolvePath(path);

    const response = await this._fetch(url, {
      method: 'PUT',
      headers: {
        ...this._getHeaders(),
        'Content-Type': 'application/octet-stream',
      },
      body: data,
    });

    if (!response.ok && response.status !== 201 && response.status !== 204) {
      throw new Error(`PUT failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Create a directory
   * @param {string} path
   * @returns {Promise<void>}
   */
  async createDirectory(path) {
    const url = this._resolvePath(path);

    const response = await this._fetch(url, {
      method: 'MKCOL',
      headers: this._getHeaders(),
    });

    if (!response.ok && response.status !== 201) {
      throw new Error(`MKCOL failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Load the static webdav_index.json for fast searching
   * @param {boolean} force - Force cache invalidation
   * @returns {Promise<Array<string>>}
   */
  async loadIndex(force = false) {
    if (force) {
      this._indexCache = null;
    }
    if (this._indexCache) {
      // Trigger background fetch to keep it fresh
      this._fetchIndex().then(data => {
        if (data) this._indexCache = data;
      }).catch(console.error);
      return this._indexCache;
    }
    
    this._indexCache = await this._fetchIndex();
    if (!this._indexCache) {
      throw new Error(`Failed to load search index from ${this._resolvePath(this._indexPath)}. Please check the file path and ensure CORS is enabled.`);
    }
    return this._indexCache;
  }

  async _fetchIndex() {
    const url = this._resolvePath(this._indexPath);
    try {
      const response = await this._fetch(url, {
        method: 'GET',
        headers: this._getHeaders(),
        cache: 'no-cache' // Will utilize ETag/Last-Modified for 304 Not Modified
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      console.warn("Failed to fetch webdav_index.json:", e);
    }
    return null;
  }

  /**
   * Delete a file or directory
   * @param {string} path
   * @returns {Promise<void>}
   */
  async deleteItem(path) {
    const url = this._resolvePath(path);

    const response = await this._fetch(url, {
      method: 'DELETE',
      headers: this._getHeaders(),
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`DELETE failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Move/rename a file or directory
   * @param {string} fromPath
   * @param {string} toPath
   * @returns {Promise<void>}
   */
  async moveItem(fromPath, toPath) {
    const url = this._resolvePath(fromPath);
    const destUrl = this._resolvePath(toPath);

    const response = await this._fetch(url, {
      method: 'MOVE',
      headers: {
        ...this._getHeaders(),
        'Destination': destUrl,
        'Overwrite': 'F',
      },
    });

    if (!response.ok && response.status !== 201 && response.status !== 204) {
      throw new Error(`MOVE failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Check if a path exists
   * @param {string} path
   * @returns {Promise<boolean>}
   */
  async exists(path) {
    const url = this._resolvePath(path);
    try {
      const response = await this._fetch(url, {
        method: 'HEAD',
        headers: this._getHeaders(),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // ---- Connection Profiles ----

  /**
   * Save a connection profile
   * @param {{ name: string, url: string, username: string, password: string }} profile
   */
  saveProfile(profile) {
    const profiles = this.loadProfiles();
    const idx = profiles.findIndex((p) => p.name === profile.name);
    const saved = {
      name: profile.name,
      url: profile.url,
      username: profile.username,
      password: this._obfuscate(profile.password),
      indexPath: profile.indexPath || '/webdav_index.json',
    };
    if (idx >= 0) {
      profiles[idx] = saved;
    } else {
      profiles.push(saved);
    }
    saveJSON(PROFILES_KEY, profiles);
  }

  /**
   * Load saved profiles
   * @returns {Array<{ name: string, url: string, username: string, password: string }>}
   */
  loadProfiles() {
    const profiles = loadJSON(PROFILES_KEY, []);
    return profiles.map((p) => {
      let pwd = '';
      if (p.password) {
        pwd = this._deobfuscate(p.password);
      }
      return {
        name: p.name,
        url: p.url,
        username: p.username,
        password: pwd,
        indexPath: p.indexPath || '/webdav_index.json',
      };
    });
  }

  // ---- Obfuscation Helpers ----

  /**
   * Basic obfuscation to prevent casual shoulder-surfing of localStorage.
   * This is not secure encryption! It's just client-side obfuscation.
   * @param {string} str 
   * @returns {string}
   */
  _obfuscate(str) {
    if (!str) return '';
    const encoded = encodeURIComponent(str);
    const key = 'mypad_webdav_v1';
    let xor = '';
    for (let i = 0; i < encoded.length; i++) {
      xor += String.fromCharCode(encoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(xor);
  }

  /**
   * Deobfuscates password with backwards compatibility for old saved profiles
   * @param {string} str 
   * @returns {string}
   */
  _deobfuscate(str) {
    if (!str) return '';
    try {
      const xor = atob(str);
      const key = 'mypad_webdav_v1';
      let decoded = '';
      for (let i = 0; i < xor.length; i++) {
        decoded += String.fromCharCode(xor.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return decodeURIComponent(decoded);
    } catch (e1) {
      // Fallback 1: Old Base64 + URI encode
      try { return decodeURIComponent(atob(str)); } 
      catch (e2) {
        // Fallback 2: Old Base64
        try { return atob(str); } 
        // Fallback 3: Plaintext
        catch (e3) { return str; }
      }
    }
  }

  /**
   * Delete a profile
   * @param {string} name
   */
  deleteProfile(name) {
    const profiles = loadJSON(PROFILES_KEY, []);
    const filtered = profiles.filter((p) => p.name !== name);
    saveJSON(PROFILES_KEY, filtered);
  }

  // ---- Internal Helpers ----

  /**
   * Resolve a path to a full URL
   * @param {string} path
   * @returns {string}
   */
  _resolvePath(path) {
    if (!this._baseUrl) throw new Error('Not connected');
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    return this._baseUrl + cleanPath;
  }

  /**
   * Get common request headers
   * @returns {Object}
   */
  _getHeaders() {
    const headers = {};
    if (this._authHeader) {
      headers['Authorization'] = this._authHeader;
    }
    return headers;
  }

  async _fetch(url, options = {}) {
    try {
      // Try direct connection first (requires CORS if cross-origin)
      const res = await fetch(url, options);
      return res;
    } catch (e) {
      // If direct fetch fails (usually CORS or mixed-content), fallback to backend proxy
      console.warn('Direct fetch failed, falling back to /api/proxy:', e);
      const proxyOptions = {
        ...options,
        headers: {
          ...(options.headers || {}),
          'X-Proxy-Target': url,
        },
      };
      
      let proxyRes;
      try {
        proxyRes = await fetch('/api/proxy', proxyOptions);
      } catch (proxyErr) {
        throw new Error(`Direct connection failed (${e.message}), and proxy is unreachable.`);
      }

      // If the proxy returns 404 or 405, it means the backend proxy is not running
      // (e.g. running on Cloudflare Pages static hosting)
      if (proxyRes.status === 404 || proxyRes.status === 405) {
        throw new Error(`Direct connection blocked by browser (CORS). Backend proxy is unavailable (${proxyRes.status}). Please enable CORS on your WebDAV server.`);
      }

      return proxyRes;
    }
  }

  /**
   * Parse PROPFIND XML response
   * @param {string} xmlText
   * @param {string} requestPath
   * @returns {Array}
   */
  _parsePropfindResponse(xmlText, requestPath) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'application/xml');

    const responses = doc.querySelectorAll('response');
    const items = [];

    // Normalize request path for comparison
    const normReqPath = this._normalizePath(requestPath);

    responses.forEach((resp) => {
      const href = resp.querySelector('href')?.textContent || '';
      const normHref = this._normalizePath(decodeURIComponent(href));

      // Skip the directory itself (first response is usually the requested dir)
      if (normHref === normReqPath || normHref + '/' === normReqPath || normReqPath + '/' === normHref) {
        return;
      }

      const displayName = resp.querySelector('displayname')?.textContent || '';
      const contentLength = resp.querySelector('getcontentlength')?.textContent || '0';
      const lastModified = resp.querySelector('getlastmodified')?.textContent || '';
      const contentType = resp.querySelector('getcontenttype')?.textContent || '';
      const resourceType = resp.querySelector('resourcetype');
      const isDirectory = resourceType?.querySelector('collection') !== null;

      // Extract name from href if displayname is empty
      const name = displayName || this._getNameFromPath(normHref);

      items.push({
        name,
        path: normHref,
        isDirectory,
        size: parseInt(contentLength, 10) || 0,
        lastModified,
        contentType,
      });
    });

    // Sort: directories first, then alphabetically
    items.sort((a, b) => {
      if (a.isDirectory && !b.isDirectory) return -1;
      if (!a.isDirectory && b.isDirectory) return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });

    return items;
  }

  /**
   * Normalize a path
   * @param {string} path
   * @returns {string}
   */
  _normalizePath(path) {
    // Remove base URL prefix if present
    if (this._baseUrl) {
      try {
        const baseUrlObj = new URL(this._baseUrl);
        if (path.startsWith(baseUrlObj.pathname)) {
          path = path.slice(baseUrlObj.pathname.length);
        }
      } catch {}
    }
    // Remove trailing slash, ensure leading slash
    path = path.replace(/\/+$/, '') || '/';
    if (!path.startsWith('/')) path = '/' + path;
    return path;
  }

  /**
   * Get filename from a path
   * @param {string} path
   * @returns {string}
   */
  _getNameFromPath(path) {
    const clean = path.replace(/\/+$/, '');
    const parts = clean.split('/');
    return parts[parts.length - 1] || '/';
  }
}
